import { useState } from 'react';
import { dbSurgeries, dbNotes } from '../../../lib/localDB';

interface ExportModalProps {
  onClose: () => void;
}

type ExportFormat = 'json' | 'csv';

export default function ExportModal({ onClose }: ExportModalProps) {
  const [format, setFormat] = useState<ExportFormat>('json');
  const [exporting, setExporting] = useState(false);
  const [done, setDone] = useState(false);

  const formatDate = (iso: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('tr-TR');
  };

  const escapeCSV = (val: unknown): string => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const exportJSON = async () => {
    const [surgeries, notes] = await Promise.all([
      dbSurgeries.getAll(),
      dbNotes.getAll(),
    ]);
    const data = {
      exportDate: new Date().toISOString(),
      version: '2.0',
      counts: { surgeries: surgeries.length, teeth: surgeries.reduce((s, c) => s + (c.teeth?.length ?? 0), 0), notes: notes.length },
      surgeries,
      notes,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vaka-kayit-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCSV = async () => {
    const [surgeries, notes] = await Promise.all([
      dbSurgeries.getAll(),
      dbNotes.getAll(),
    ]);

    // ── Vaka + Diş CSV ──
    const teethHeaders = [
      'Hasta Adı', 'Tarih', 'Cinsiyet', 'Yaş', 'Şeker', 'Sigara',
      'Diş No', 'Kemik', 'Soket', 'Greft', 'Hacim', 'İmplant Markası', 'VDK',
      'Fail', 'Fail Tarihi', 'Fail Süresi (Gün)',
    ];
    const teethRows: string[] = [];
    surgeries.forEach((s) => {
      const cinsLabel = s.cinsiyet === 'k' ? 'Kadın' : s.cinsiyet === 'e' ? 'Erkek' : s.cinsiyet;
      if (s.teeth && s.teeth.length > 0) {
        s.teeth.forEach((t) => {
          teethRows.push([
            s.patient_name,
            formatDate(s.operation_date),
            cinsLabel,
            s.yas,
            s.seker,
            s.sigara,
            t.tooth_number,
            t.kemik,
            t.soket,
            t.greft,
            t.hacim,
            t.implant_brand || '',
            t.vdk,
            t.failed ? 'Evet' : 'Hayır',
            t.fail_date ? formatDate(t.fail_date) : '',
            t.fail_days != null ? t.fail_days : '',
          ].map(escapeCSV).join(','));
        });
      } else {
        teethRows.push([
          s.patient_name,
          formatDate(s.operation_date),
          cinsLabel,
          s.yas,
          s.seker,
          s.sigara,
          '', '', '', '', '', '', '', '', '', '',
        ].map(escapeCSV).join(','));
      }
    });
    const teethCSV = [teethHeaders.map(escapeCSV).join(','), ...teethRows].join('\n');

    // ── Not CSV ──
    const patientNameMap = new Map(surgeries.map((s) => [s.id, s.patient_name]));
    const noteHeaders = ['Hasta Adı', 'Tarih', 'Not Türü', 'Başlık', 'İçerik'];
    const noteTypeMap: Record<string, string> = {
      general: 'Genel', checkup: 'Kontrol', complication: 'Komplikasyon',
      treatment: 'Tedavi', followup: 'Takip',
    };
    const noteRows = notes.map((n) =>
      [patientNameMap.get(n.patient_id) ?? n.patient_id, formatDate(n.note_date), noteTypeMap[n.note_type] ?? n.note_type, n.title, n.content]
        .map(escapeCSV).join(',')
    );
    const notesCSV = [noteHeaders.map(escapeCSV).join(','), ...noteRows].join('\n');

    const combined = 'VAKALAR VE DİŞLER\n' + teethCSV + '\n\n\nNOTLAR\n' + notesCSV;
    const blob = new Blob(['\uFEFF' + combined], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vaka-kayit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      if (format === 'json') await exportJSON();
      else await exportCSV();
      setDone(true);
      setTimeout(() => { setDone(false); onClose(); }, 1800);
    } catch (e) {
      console.error('Export hatası:', e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Verileri Dışa Aktar</h3>
            <p className="text-sm text-gray-500 mt-0.5">Tüm kayıtlarınızı yedekleyin</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer transition-all">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">Format Seçin</p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setFormat('json')}
                className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${format === 'json' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-braces-line text-xl text-teal-600"></i>
                </div>
                <p className={`font-semibold text-sm ${format === 'json' ? 'text-teal-700' : 'text-gray-900'}`}>JSON</p>
                <p className="text-xs text-gray-500 mt-0.5">Tam veri yedeği</p>
                <p className="text-xs text-gray-400 mt-1">Geri yükleme için ideal</p>
              </button>
              <button onClick={() => setFormat('csv')}
                className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${format === 'csv' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-table-line text-xl text-emerald-600"></i>
                </div>
                <p className={`font-semibold text-sm ${format === 'csv' ? 'text-teal-700' : 'text-gray-900'}`}>CSV</p>
                <p className="text-xs text-gray-500 mt-0.5">Excel ile aç</p>
                <p className="text-xs text-gray-400 mt-1">Analiz ve raporlama için</p>
              </button>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <i className="ri-shield-check-line text-amber-500 text-lg flex-shrink-0 mt-0.5"></i>
            <div>
              <p className="text-sm font-medium text-amber-800">Güvenli Yedekleme</p>
              <p className="text-xs text-amber-700 mt-0.5">Tüm veriler sadece cihazınızda. Tarayıcı önbelleğini temizlemeden önce mutlaka yedek alın.</p>
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-3 px-4 border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer">İptal</button>
            <button onClick={handleExport} disabled={exporting || done}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer ${done ? 'bg-green-500 text-white' : 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white hover:from-teal-600 hover:to-cyan-700 disabled:opacity-50'}`}>
              {done ? (
                <><i className="ri-check-line text-lg"></i>İndirildi!</>
              ) : exporting ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Hazırlanıyor...</>
              ) : (
                <><i className="ri-download-2-line text-lg"></i>İndir ({format.toUpperCase()})</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
